import React, { createContext, useContext, useEffect, useState } from "react";
import { useAuth } from "../auth/AuthContext";
import { PROPERTY_OPTIONS, type PropertyOption } from "../auth/accessGrants";
import { getValidJwtToken } from "../auth/authSession";
import { fetchProperties } from "./propertiesApi";
import { getActivePropertyId, setActivePropertyId } from "./propertyContext";

// Initialise with the static fallback so consumers always have a value.
const PropertiesCtx = createContext<PropertyOption[]>(PROPERTY_OPTIONS);

/**
 * Loads the property list from the OPS DB once the user is authenticated and
 * a JWT token is present.  Falls back silently to the static PROPERTY_OPTIONS
 * constant when the server is unavailable or the local auth provider is in use
 * (local auth issues no JWT, so the fetch is skipped entirely).
 *
 * Place this inside <AuthProvider> so it can call useAuth().
 */
export function PropertiesProvider({ children }: { children: React.ReactNode }) {
  const { status } = useAuth();
  const [options, setOptions] = useState<PropertyOption[]>(PROPERTY_OPTIONS);

  useEffect(() => {
    // Reset to static list on logout / unauthenticated state.
    if (status !== "authed") {
      setOptions(PROPERTY_OPTIONS);
      return;
    }

    // Skip the fetch when no JWT is present (local in-memory auth provider).
    // This avoids a noisy 401 on every page load in development.
    if (!getValidJwtToken()) return;

    let cancelled = false;

    fetchProperties()
      .then((remote) => {
        if (cancelled || remote.length === 0) return;
        // Map to the lighter PropertyOption shape (drop `code`).
        const nextOptions = remote.map(({ id, label, code, isDefault }) => ({
          id,
          label: code?.trim() && label.trim() ? `${code.trim()} / ${label.trim()}` : label,
          isDefault,
        }));
        setOptions(nextOptions);

        const activePropertyId = getActivePropertyId(nextOptions);
        if (activePropertyId) {
          setActivePropertyId(activePropertyId, nextOptions);
        }
      })
      .catch((err: unknown) => {
        console.warn(
          "[properties] Failed to load properties from server — using static fallback.",
          err instanceof Error ? err.message : err,
        );
        // Leave the current options (either static fallback or a previous successful fetch).
      });

    return () => {
      cancelled = true;
    };
  }, [status]);

  return <PropertiesCtx.Provider value={options}>{children}</PropertiesCtx.Provider>;
}

/**
 * Returns the live property list from the OPS DB.
 * Falls back to PROPERTY_OPTIONS when no server data has been loaded yet
 * (local auth, offline, or OPS DB not configured).
 *
 * Must be called inside a component that is a descendant of <PropertiesProvider>.
 */
export function useAllPropertyOptions(): PropertyOption[] {
  return useContext(PropertiesCtx);
}
